import React from "react";
import {View, Text} from "@unibuddy/patron";
import {useAnalytics} from "@unibuddy/tracking";

import {LoadingCover} from "ubcommunity-shared/src/General/LoadingCover/LoadingCover";
import {useGetInviteInformationQuery} from "ubcommunity-shared/src/types";
import {SignUpPrompt} from "ubcommunity-shared/src/Community/SignUpPrompt";
import {
    TrackEvents,
    SignUpTrackingPageSources,
    SignUpSources,
} from "ubcommunity-shared/src/constants";
import {Routes} from "ubcommunity-shared/src/constants/routes";
import {QueryErrorHandler} from "ubcommunity-shared/src/General/Errors/QueryErrorHandler";
import {SUPPORT_EMAIL} from "ubcommunity-shared/src/constants/constants";
import {SimpleFallback} from "ubcommunity-shared/src/General/Errors/SimpleFallback";
import {Link} from "ubcommunity-shared/src/General/Link/Link";
import {GraphQLErrors} from "ubcommunity-shared/src/constants/errors";

interface Props {
    personalisedInviteId: string;
    source?: SignUpSources;
    handleSubmit?: () => void;
}

export const LandingV2 = ({personalisedInviteId, source, handleSubmit}: Props) => {
    const {trackEvent} = useAnalytics();

    const {data, loading, error, refetch} = useGetInviteInformationQuery({
        variables: {
            inviteInformationInput: {
                inviteId: personalisedInviteId,
            },
        },
    });

    const inviteInformation = data?.getInviteInformation;

    if (!data && error) {
        if (error?.graphQLErrors[0]?.extensions?.code === GraphQLErrors.NOT_FOUND) {
            return (
                <SimpleFallback
                    errorMessage="Oops! This invite is invalid or the community has been archived."
                    hideIllustration
                >
                    <Text size="medium" align="center">
                        Please contact your university administrator for more information, or email
                        us at <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
                    </Text>
                </SimpleFallback>
            );
        }
        return (
            <QueryErrorHandler
                error={error}
                retryCallback={refetch}
                meta={{component: "LandingV2", query: "useGetInviteInformationQuery"}}
                layout="center"
            />
        );
    }

    if (loading) {
        return <LoadingCover loading backgroundColor="body" />;
    }

    const handleClick = () => {
        handleSubmit();
        trackEvent(TrackEvents.CREATE_YOUR_ACCOUNT_CLICKED, {
            source: SignUpTrackingPageSources.LANDING_V2,
        });
    };

    return (
        <View
            backgroundColor="transparent"
            style={{zIndex: 1}}
            padding={["none", "none", "small"]}
            alignItems="center"
            maxW={450}
        >
            <SignUpPrompt
                linkInfo={{
                    id: inviteInformation.id,
                    squareLogo: inviteInformation.university.squareLogo,
                    communityTitle:
                        inviteInformation.community.title || inviteInformation.university.name,
                    valid: true,
                    universitySlug: inviteInformation.university.slug,
                }}
                userDataLoading={loading}
                communityDescription={inviteInformation.community.description}
                handleSubmit={handleClick}
                pageSource={SignUpTrackingPageSources.LANDING_V2}
                loginPath={`${Routes.LOGIN}?source=${source}`}
            />
        </View>
    );
};
